import React, { useEffect, useMemo, useState } from 'react';
import { PageLayout } from '../components/PageLayout';
import { PrimaryButton } from '../components/PrimaryButton';
import { useLanguage } from '../i18n/LanguageContext';
import { listTakeoutMenus, type TakeoutMenu } from '../api/takeoutMenu';
import type { OrderItemState } from '../hooks/useKioskFlow';
import styles from './MenuSelectionPage.module.css';

interface MenuSelectionPageProps {
  storeId: string;
  orderItems: OrderItemState[];
  onSetQuantity: (menuId: string, quantity: number) => void;
  onNext: () => void;
  onBack: () => void;
  takeoutEnabled?: boolean;
  waitingCount?: number;
  estimatedMinutes?: number;
  takeoutWaitingCount?: number;
  takeoutEstimatedMinutes?: number;
  storeImage?: string | null;
  isDemo?: boolean;
}

const DEMO_MENUS: TakeoutMenu[] = [
  { id: 'demo-1', storeId: 'demo', name: '비빔밥', description: '신선한 나물과 고추장을 넣어 비벼 드세요.', price: 8000, isActive: true, displayOrder: 1 },
  { id: 'demo-2', storeId: 'demo', name: '불고기 도시락', description: '달콤한 양념 불고기와 공깃밥 구성.', price: 9500, isActive: true, displayOrder: 2 },
  { id: 'demo-3', storeId: 'demo', name: '김치찌개', description: '돼지고기와 묵은지로 진하게 끓인 찌개.', price: 7500, isActive: true, displayOrder: 3 },
  { id: 'demo-4', storeId: 'demo', name: '제육덮밥', description: '매콤한 제육볶음을 덮밥 스타일로 제공.', price: 8500, isActive: true, displayOrder: 4 },
];

export function MenuSelectionPage({
  storeId,
  orderItems,
  onSetQuantity,
  onNext,
  onBack,
  takeoutEnabled,
  waitingCount,
  estimatedMinutes,
  takeoutWaitingCount,
  takeoutEstimatedMinutes,
  storeImage,
  isDemo,
}: MenuSelectionPageProps) {
  const { t } = useLanguage();
  const [menus, setMenus] = useState<TakeoutMenu[]>(isDemo ? DEMO_MENUS : []);
  const [loading, setLoading] = useState(!isDemo);

  useEffect(() => {
    if (isDemo) return;
    let cancelled = false;
    listTakeoutMenus(storeId)
      .then((data) => {
        if (!cancelled) setMenus(data);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [storeId, isDemo]);

  const qtyMap = useMemo(() => {
    const m = new Map<string, number>();
    for (const i of orderItems) m.set(i.menuId, i.quantity);
    return m;
  }, [orderItems]);

  const total = useMemo(() => {
    let sum = 0;
    for (const item of orderItems) {
      const menu = menus.find((m) => m.id === item.menuId);
      if (menu) sum += menu.price * item.quantity;
    }
    return sum;
  }, [orderItems, menus]);

  const totalQty = orderItems.reduce((s, i) => s + i.quantity, 0);
  const canProceed = totalQty > 0;

  return (
    <PageLayout
      title={t('menu_title')}
      takeoutEnabled={takeoutEnabled}
      waitingCount={waitingCount}
      estimatedMinutes={estimatedMinutes}
      takeoutWaitingCount={takeoutWaitingCount}
      takeoutEstimatedMinutes={takeoutEstimatedMinutes}
      storeImage={storeImage}
      footer={
        <div className={styles.footer}>
          <div className={styles.totalRow}>
            <span className={styles.totalLabel}>{t('menu_total')}</span>
            <span className={styles.totalValue}>{total.toLocaleString()}원</span>
          </div>
          <div className={styles.buttonRow}>
            <button type="button" className={styles.backBtn} onClick={onBack}>
              &lt; {t('menu_prev')}
            </button>
            <div className={styles.nextWrapper}>
              <PrimaryButton onClick={onNext} disabled={!canProceed}>
                {t('menu_next')} &gt;
              </PrimaryButton>
            </div>
          </div>
        </div>
      }
    >
      <div className={styles.content}>
        {loading ? (
          <div className={styles.empty}>...</div>
        ) : menus.length === 0 ? (
          <div className={styles.empty}>{t('menu_empty')}</div>
        ) : (
          <div className={styles.list}>
            {menus.map((menu) => {
              const qty = qtyMap.get(menu.id) ?? 0;
              return (
                <div key={menu.id} className={styles.item}>
                  {/* 상단: 메뉴명 ... 가격 */}
                  <div className={styles.topRow}>
                    <span className={styles.itemName}>{menu.name}</span>
                    <span className={styles.itemPrice}>{menu.price.toLocaleString()}원</span>
                  </div>
                  {/* 하단: 메뉴 설명 ... - 0 + */}
                  <div className={styles.bottomRow}>
                    <span className={styles.itemDescription}>{menu.description || ''}</span>
                    <div className={styles.qtyControls}>
                      <button
                        type="button"
                        className={styles.qtyBtn}
                        onClick={() => onSetQuantity(menu.id, Math.max(0, qty - 1))}
                        disabled={qty === 0}
                        aria-label="감소"
                      >
                        −
                      </button>
                      <span className={styles.qtyValue}>{qty}</span>
                      <button
                        type="button"
                        className={styles.qtyBtn}
                        onClick={() => onSetQuantity(menu.id, qty + 1)}
                        aria-label="증가"
                      >
                        +
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </PageLayout>
  );
}
